import { Button } from "@/components/Button";
import {
  useGetJWTForAccount,
  usePostLogout,
  useRelatedCustomers,
  useUser,
} from "@/requests/hooks/user";
import {
  faBuilding,
  faSearch,
  faSpinnerThird,
  faUser,
} from "@fortawesome/pro-duotone-svg-icons";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { H3 } from "@/components/Typography";
import { logoutSuccessCallback } from "@/requests/callbacks/user";
import parseQueryString from "@/utils/parseQueryString";
import { INTEGRATIONS_CHECKLIST_URL } from "@/utils/const";

const CompanySelector = () => {
  const queryClient = useQueryClient();
  const { data: customers, isFetching: isFetchingGetRelatedCustomers } =
    useRelatedCustomers();
  const { mutate: logout, isSuccess: isLogoutSuccess } = usePostLogout();
  const {
    mutate: postJWTForAccountMutate,
    isPending,
    isSuccess: isGetJWTForAccountSuccess,
  } = useGetJWTForAccount();
  const { data: userData, isRefetching: isUserRefetching, refetch } = useUser();

  const [search, setSearch] = useState<string>("");
  const [selectedMerchant, setSelectedMerchant] = useState<number>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLogoutSuccess) logoutSuccessCallback(queryClient, navigate);
  }, [isLogoutSuccess]);

  useEffect(() => {
    if (isGetJWTForAccountSuccess) {
      navigateHome(true);
    }
  }, [isGetJWTForAccountSuccess]);

  const navigateHome = async (shouldRefetch: boolean = true) => {
    queryClient.removeQueries();

    if (shouldRefetch) await refetch();

    const { redirect } = parseQueryString();
    if (redirect) {
      navigate(redirect);
    } else {
      navigate(INTEGRATIONS_CHECKLIST_URL);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(e.target.value);

  const onClickLogout = () => {
    logout();
  };

  const onClickCustomer = (merchantId: number) => () => {
    setSelectedMerchant(merchantId);
    if (merchantId === userData?.merchantId) {
      /**
       * If the logged in account is in Onboarding, we should redirect
       * the user there, not the dashboard.
       */
      if (!!userData?.activeOnboardingFlow) {
        postJWTForAccountMutate({
          merchant_id: merchantId,
          integration: "?integration=SHOPIFY",
        });

        navigate("/onboarding/marketing");
        return;
      }

      navigateHome(true);
    } else {
      postJWTForAccountMutate({
        merchant_id: merchantId,
        integration: "?integration=SHOPIFY",
      });
    }
  };

  const renderTitle = () => (
    <div className="flex flex-col items-start justify-center gap-10">
      <div className="flex flex-row items-center gap-10">
        <FontAwesomeIcon
          fixedWidth
          className="h-15 w-15 text-secondary"
          icon={faUser}
        />
        <p className="text-gray-dark">{userData?.email}</p>
      </div>
      <H3 className="font-semibold">{t("auth.company_selector.heading")}</H3>
    </div>
  );

  const renderAccountItem = ({ merchantId, companyName }: IAccount) => (
    <button
      key={merchantId}
      className="group flex w-full cursor-pointer snap-start flex-row items-center justify-start gap-20 overflow-x-clip whitespace-break-spaces break-all border-b border-gray px-10 py-20 text-left last:border-none hover:text-primary"
      onClick={onClickCustomer(merchantId)}
      disabled={isPending || isUserRefetching}
      data-testid="customer-account">
      <FontAwesomeIcon
        icon={faBuilding}
        className="h-20 w-20 text-gray-dark group-hover:text-primary"
      />
      <div className="flex flex-grow">{companyName}</div>
      {(isPending || isUserRefetching) && selectedMerchant === merchantId ? (
        <FontAwesomeIcon
          icon={faSpinnerThird}
          spin={true}
          className="h-20 w-20"
        />
      ) : (
        <FontAwesomeIcon
          icon={faCaretRight}
          className="h-15 w-15"
          color="inherit"
        />
      )}
    </button>
  );

  const renderSearchBar = () => (
    <div className="mt-10 flex flex-row items-center gap-10 border-y border-gray py-10 pr-10">
      <input
        role="search"
        className="flex-grow appearance-none border-none bg-transparent px-10 py-10 shadow-none outline-none"
        onChange={handleSearch}
        value={search}
        placeholder={t("auth.company_selector.search_placeholder")}
      />
      <FontAwesomeIcon icon={faSearch} className="h-15 w-15 text-secondary" />
    </div>
  );

  const renderCustomers = () => {
    const filteredCustomers = customers?.results?.filter(customer =>
      customer.companyName.toLowerCase().includes(search.toLowerCase()),
    );

    if (!filteredCustomers?.length)
      return (
        <div className="flex flex-col items-center gap-20 px-10 py-20 text-gray-dark">
          <FontAwesomeIcon
            icon={faSearch}
            className="h-100 w-100"
            color="inherit"
          />
          {t("auth.company_selector.no_results", { search })}
        </div>
      );

    return (
      <div className="max-h-[284px] snap-y snap-proximity overflow-scroll border-b border-gray">
        {filteredCustomers?.map(account => renderAccountItem(account))}
      </div>
    );
  };

  const renderCustomerList = () =>
    isFetchingGetRelatedCustomers ? (
      <div className="flex items-center justify-center py-20">
        <FontAwesomeIcon
          className="h-30 w-30"
          icon={faSpinnerThird}
          spin={true}
        />
      </div>
    ) : (
      <>
        {renderSearchBar()}
        {renderCustomers()}
      </>
    );

  const renderButtons = () => (
    <div className="mt-20 flex flex-col items-center gap-20">
      <Button
        variant="link-light"
        onClick={onClickLogout}
        trackingId={{ eventName: "company-selector.back-to-signin" }}
        width="unset">
        {t("auth.company_selector.buttons.back")}
      </Button>
    </div>
  );
  return (
    <>
      {renderTitle()}
      {renderCustomerList()}
      {renderButtons()}
    </>
  );
};

export default CompanySelector;
